<template>
  <div class="container p-4 max-w-5xl">
    <h1 class="text-3xl font-bold mb-6">{{ titre }}</h1>

    <div v-if="loading" class="mb-4">Chargement...</div>
    <div v-if="message" class="mb-4 text-red-600">{{ message }}</div>

    <ul v-if="!loading && chats.length > 0">
      <li
        v-for="chat in chats"
        :key="chat.id"
        class="mb-3 p-3 border rounded flex justify-end items-center gap-4"
      >
        <div class="flex-1">
          <strong>{{ chat.nom }}</strong> (#{{ chat.no_identification }}) -
          {{ chat.type || "Type inconnu" }}
        </div>
        <button
          @click="ouvrirEdition(chat)"
          class="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          aria-label="Modifier le chat" title="Modifier les informations du chat"
        >
          Modifier
        </button>
        <button
          @click="demanderSuppression(chat)"
          class="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 ml-2"
          aria-label="Supprimer le chat" title="Supprimer le chat"
        >
          Supprimer
        </button>
      </li>
    </ul>

    <div v-if="!loading && chats.length === 0">Aucun chat disponible.</div>

    <!-- Modale édition -->
    <div
      v-if="chatEnEdition"
      class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      @click.self="fermerEdition"
    >
      <div
        class="bg-white rounded-lg shadow-lg mx-2 p-2 sm:p-6 w-full max-w-lg relative max-h-[90vh] overflow-y-auto"
        @click.stop
      >
        <button
          @click="fermerEdition"
          class="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl font-bold"
          aria-label="Fermer modale"
          title="Fermer la modale de modification"
        >
          &times;
        </button>
        <h2 class="text-xl font-bold mb-4">Modifier le chat</h2>
        <FormulaireChat
          :chat-initial="chatEnEdition"
          mode="modification"
          @sauvegarder="sauvegarderModifications"
          @annuler="fermerEdition"
          @fermer="fermerEdition"
          @supprimer="demanderSuppression(chatEnEdition)"
        />
      </div>
    </div>

    <!-- Modale de confirmation suppression -->
    <div
      v-if="confirmationSuppressionVisible && chatASupprimer"
      class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[2000]"
      @click.self="annulerSuppression"
    >
      <div
        class="bg-white rounded-lg shadow-lg mx-2 p-6 w-full max-w-md relative"
        @click.stop
      >
        <button
          @click="annulerSuppression"
          class="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl font-bold"
          aria-label="Fermer modale" title="Fermer la modale de confirmation"
        >
          &times;
        </button>
        <div class="mb-4 text-lg">
          Êtes-vous sûr de vouloir supprimer le chat #{{
            chatASupprimer.no_identification
          }}
          ?
        </div>
        <div class="flex justify-end gap-2">
          <button
            @click="confirmerSuppression"
            class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            aria-label="Confirmer la suppression" title="Confirmer la suppression du chat"
          >
            Oui
          </button>
          <button
            @click="annulerSuppression"
            class="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            aria-label="Annuler la suppression" title="Annuler la suppression du chat"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch} from "vue";
import { auth } from "../firebase.js";
import { useListeChats } from "@/composables/apiListeChats.js";
import FormulaireChat from "./FormulaireChat.vue";

const props = defineProps({
  apiUrl: {
    type: String,
  },
  titre: {
    type: String,
    default: "Liste des Chats",
  },
});


const {
  listeChats,
  message,
  chargerlisteChats,
  mettreAJourPartiellement,
  supprimer,
} = useListeChats();

const chats = ref([]);
const loading = ref(true);
const chatEnEdition = ref(null);

// Pour la modale de suppression
const chatASupprimer = ref(null);
const confirmationSuppressionVisible = ref(false);

async function charger() {
  loading.value = true;
  if (props.apiUrl) {
    try {
      const response = await fetch(props.apiUrl);
      if (!response.ok) throw new Error("Erreur de chargement des données");
      const data = await response.json();

      // Pour les femelles, mâles, adoptés, craintifs et chatons : objet dont les valeurs sont les chats
      if (
        props.apiUrl.includes('chats_femelles') ||
        props.apiUrl.includes('chats_males') ||
        props.apiUrl.includes('chats_adoptes') ||
        props.apiUrl.includes('chats_craintifs') ||
        props.apiUrl.includes('chatons_0_5_mois') ||
        props.apiUrl.includes('chatons_6_12_mois')
      ) {
        chats.value = Object.values(data);
      }
      // Pour la liste complète : data.listeChats.data
      else if (data.listeChats && Array.isArray(data.listeChats.data)) {
        chats.value = data.listeChats.data;
      }
      // Autre cas : tente de trouver un tableau
      else if (Array.isArray(data)) {
        chats.value = data;
      } else if (Array.isArray(data.data)) {
        chats.value = data.data;
      } else {
        chats.value = [];
      }
    } catch (e) {
      chats.value = [];
      message.value = `Erreur : ${e.message}`;
    }
  } else {
    await chargerlisteChats();
    chats.value = listeChats.value;
  }
  loading.value = false;
}

onMounted(() => {
  charger();
});

// REcharge si apiUrl change
watch(() => props.apiUrl, () => {
  charger();
});

// Ouvre la modale d'édition en clonant l'objet chat pour éviter les modifications directes
function ouvrirEdition(chat) {
  chatEnEdition.value = { ...chat };
}

function fermerEdition() {
  chatEnEdition.value = null;
}

async function sauvegarderModifications(chatModifie) {
  try {
    const user = auth.currentUser;
    if (!user) {
      alert("Vous devez être connecté pour modifier un chat.");
      return;
    }
    const token = await user.getIdToken(true);

    // Conversion oui/non en 1/0 pour le champ reserve
    if (chatModifie.reserve === "oui") {
      chatModifie.reserve = 1;
    } else if (chatModifie.reserve === "non") {
      chatModifie.reserve = 0;
    }
    const result = await mettreAJourPartiellement(
      chatModifie.id,
      chatModifie,
      token
    );

    if (
      result.success === true ||
      (result.message && result.message.toLowerCase().includes("succès"))
    ) {
      await chargerlisteChats();
      fermerEdition();
      alert("Modifications sauvegardées avec succès.");
    } else {
      alert(`Erreur : ${result.message}`);
    }
  } catch (error) {
    console.error("Erreur lors de la sauvegarde:", error);
    alert("Erreur lors de la sauvegarde. Veuillez réessayer.");
  }
}

// Ouvre la modale de confirmation de suppression

function demanderSuppression(chat) {
  chatASupprimer.value = { ...chat };
  ouvrirConfirmationSuppression();
}

function ouvrirConfirmationSuppression() {
  confirmationSuppressionVisible.value = true;
}

function annulerSuppression() {
  confirmationSuppressionVisible.value = false;
  chatASupprimer.value = null;
}

async function confirmerSuppression() {
  if (!chatASupprimer.value) return;
  try {
    const user = auth.currentUser;
    if (!user) {
      alert("Vous devez être connecté pour supprimer un chat.");
      return;
    }
    const token = await user.getIdToken(true);
    await supprimer(chatASupprimer.value.id, token);
    await chargerlisteChats();
    alert("Chat supprimé avec succès.");
  } catch (error) {
    console.error("Erreur lors de la suppression:", error);
    alert("Erreur lors de la suppression. Veuillez réessayer.");
  } finally {
    confirmationSuppressionVisible.value = false;
    chatASupprimer.value = null;
  }
}
</script>

<style scoped>
/* Ajoute ici tes styles personnalisés si besoin */
</style>
